import classNames from "classnames";
import { useFormikContext } from "formik";
import React, { useEffect, useRef, useState } from "react";
import useTranslations from "../../hooks/useTranslations";
import { isNullOrWhiteSpace } from "../../shared/string";
import Button, { ButtonProps } from "../Buttons/Button";

export type SubmitButtonProps = ButtonProps & {
  submitInvalidText?: string;
  submitText?: string;
  submittingText?: string;
  submitCompleteText?: string;
  submitting?: boolean;
  submitComplete?: boolean;
  widthClassName?: string;
};

const SubmitButton = (props: SubmitButtonProps) => {
  const {
    submitInvalidText,
    submitText,
    submitCompleteText,
    submittingText,
    submitting,
    submitComplete,
    widthClassName = "w-56",
    ...buttonProps
  } = props;

  const context = useFormikContext();
  const translations = useTranslations();
  const [pauseAnimation, setPauseAnimation] = useState(false);
  const [timerRunning, setTimerRunning] = useState(false);
  const [loadingBarCompletelyFinished, setLoadingBarCompletelyFinished] = useState(false);
  const [submitTimer, setSubmitTimer] = useState<number>();

  let className = classNames(
    widthClassName,
    "relative overflow-hidden anticimex-button-style--border-thick"
  );
  let text: string;

  // Use a ref here to keep track of submitComplete status inside the useEffect, but without
  // adding it to the dependency array
  const submitCompleteRef = useRef(submitComplete);

  // These useEffects do two things.
  // 1. Make sure that we always show a loading bar for 2 seconds, no matter if the submit completed or not.
  // 2. After 1.8 seconds, if the submit is not yet finished, pause the loading bar until it has.
  useEffect(() => {
    if (submitting) {
      setTimerRunning(true);
      setSubmitTimer(
        window.setTimeout(() => {
          if (submitCompleteRef.current === false) {
            setPauseAnimation(true);
            setSubmitTimer(
              window.setTimeout(() => {
                setTimerRunning(false);
              }, 200)
            );
          } else {
            setTimerRunning(false);
          }
        }, 1800)
      );
    }
  }, [submitting]);

  // Clear timer on unmount
  useEffect(
    () => () => {
      if (submitTimer) {
        clearTimeout(submitTimer);
      }
    },
    [submitTimer]
  );

  useEffect(() => {
    submitCompleteRef.current = submitComplete;

    if (submitComplete) {
      setPauseAnimation(false);

      // Finish up the remaining 10% of the loading bar animation
      const timer = setTimeout(() => {
        setLoadingBarCompletelyFinished(true);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [submitComplete, timerRunning]);

  let isValid = true;

  if (context.errors && context.touched) {
    const errorKeys = Object.keys(context.errors);
    const touchedKeys = Object.keys(context.touched);
    isValid = errorKeys.some(error => touchedKeys.includes(error)) === false;
  }

  if (submitting || timerRunning) {
    className += " submitting";
    text = !isNullOrWhiteSpace(submittingText)
      ? submittingText
      : translations.submitting ?? "";
  } else if (submitComplete && timerRunning === false) {
    if (loadingBarCompletelyFinished) {
      className += " submitComplete";
    } else {
      className += " submitting";
    }
    text = !isNullOrWhiteSpace(submitCompleteText)
      ? submitCompleteText
      : translations.submitted ?? "";
  } else if (isValid === false) {
    className += " invalid";
    text = !isNullOrWhiteSpace(submitInvalidText)
      ? submitInvalidText
      : translations.submitInvalid ?? "";
  } else {
    text = !isNullOrWhiteSpace(submitText) ? submitText : translations.submit ?? "";
  }

  return (
    <Button
      {...buttonProps}
      type="submit"
      className={`${buttonProps.className ?? ""} ${className} ${
        pauseAnimation ? "pause" : ""
      }`}
      applyDisabledClasses={false}
      disabled={isValid === false || submitting || submitComplete}
    >
      {text}
    </Button>
  );
};

export default SubmitButton;
